import { useEffect, useRef, useState, type ReactNode } from "react";
import { coachApi, ApiError } from "@/api";
import { useUI } from "@/lib/theme";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

// Render Markdown simplificado (igual que Coach.tsx)
function inline(text: string, keyBase: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyBase}-${i}`}>{p}</span>
    ),
  );
}

function renderLine(line: string, key: number): ReactNode {
  const h = /^(#{1,3})\s+(.*)$/.exec(line);
  if (h) return <p key={key} className="mb-1 mt-2 font-bold text-fg">{inline(h[2], `h${key}`)}</p>;
  const li = /^\s*[-*]\s+(.*)$/.exec(line);
  if (li) return <li key={key} className="ml-4 list-disc">{inline(li[1], `li${key}`)}</li>;
  const q = /^\s*>\s?(.*)$/.exec(line);
  if (q) return <p key={key} className="border-l-2 border-line pl-3 text-xs italic text-muted">{inline(q[1], `q${key}`)}</p>;
  if (!line.trim()) return <div key={key} className="h-2" />;
  return <p key={key}>{inline(line, `p${key}`)}</p>;
}

const SUGGESTIONS_ES = [
  "¿Cómo está la forma del atleta esta semana?",
  "Diseña un microciclo de carga para esta semana",
  "¿Es buen momento para hacer series o mejor recuperar?",
];
const SUGGESTIONS_EN = [
  "How is the athlete's form this week?",
  "Design a loading microcycle for this week",
  "Is it a good time for intervals or better to recover?",
];

export default function CoachPlanChat({ athleteId }: { athleteId: string }) {
  const { t, lang } = useUI();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [history, setHistory] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history, loading]);

  const suggestions = lang === "es" ? SUGGESTIONS_ES : SUGGESTIONS_EN;

  async function send(text: string) {
    const q = text.trim();
    if (!q || loading) return;
    setInput("");
    setError(null);
    setHistory((h) => [...h, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await coachApi.planChat(athleteId, { message: q, lang, threadId });
      setThreadId(res.threadId);
      setHistory((h) => [...h, { role: "assistant", content: res.reply }]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setError(t("coach_rate"));
      else setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="rounded-xl border border-line bg-panel/70 p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted">{t("coach_title")}</h2>
        <span className="ml-auto rounded-md bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
          plan
        </span>
      </div>

      <div className="flex min-w-0 flex-col">
        <div
          ref={scrollRef}
          className="flex h-[42vh] min-h-[250px] max-h-[480px] flex-col gap-2 overflow-y-auto pr-1"
        >
          {history.length === 0 && (
            <div className="m-auto max-w-md text-center text-sm text-muted">
              <p>
                {lang === "es"
                  ? "Pregúntame sobre la forma, la carga o el plan de este atleta."
                  : "Ask me about this athlete's form, load, or training plan."}
              </p>
            </div>
          )}
          {history.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-accent text-white" : "border border-line bg-panel2/60 text-fg"
                }`}
              >
                {m.role === "assistant" ? (
                  <div className="space-y-0.5">{m.content.split("\n").map((l, k) => renderLine(l, k))}</div>
                ) : (
                  m.content
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl border border-line bg-panel2/60 px-3 py-2 text-sm text-muted">
                {t("coach_thinking")}
              </div>
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
              {t("coach_error")}: {error}
            </div>
          )}
        </div>

        {history.length === 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => void send(s)}
                disabled={loading}
                className="rounded-lg border border-line px-3 py-1.5 text-xs text-muted transition hover:border-accent hover:text-accent disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); void send(input); }}
          className="mt-2 flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(input); }
            }}
            rows={1}
            placeholder={t("coach_placeholder")}
            className="max-h-24 min-h-[38px] flex-1 resize-none rounded-xl border border-line bg-panel px-3 py-2 text-sm text-fg outline-none transition focus:border-accent"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-40"
          >
            {t("coach_send")}
          </button>
        </form>
      </div>

      <p className="mt-1.5 text-[11px] text-muted">{t("coach_disclaimer")}</p>
    </section>
  );
}
